import StyledButton from '@/components/styled/StyledButton';
import StyledContainer from '@/components/styled/StyledContainer';
import StyledFormHeaderWithBreadcrumb from '@/components/styled/StyledFormHeaderWithBreadcrumb';
import {
  ClientFormData,
  getClientSchema,
} from '@/types/clients/ClientFormData';
import { useDistributorLabels } from '@/hooks/useDistributorLabels';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useWatch,
} from 'react-hook-form';
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react';
import { useNavigate } from 'react-router';
import StyledButtonLoader from '@/components/styled/StyledButtonLoader';
import HorizontalLine from '@/components/ui/HorizontalLine';
import { useGetMe } from '@/api/user.api';
import { useEffect, useMemo } from 'react';
import ClientForm from '@/components/clients/ClientForm';
import { useCreateClient, useFindClients } from '@/hooks/useAgencyClient';
import { useToast } from '@/hooks/useToast';
import { PATHS } from '@/constants/Paths';
import { useTranslation } from '@/i18n';
import { MarginRateSchema } from '@/types/common/margin-rate.schema';

const CreateClientPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isTourOperator, tLabel } = useDistributorLabels();

  const { data: userData, isSuccess } = useGetMe();

  const { data: agents } = useFindClients({}, (data) =>
    data.agents.map((agent) => ({
      value: agent.id,
      label: `${agent.firstname} ${agent.lastname}`,
    })),
  );

  const currentAgent = useMemo(
    () =>
      isSuccess
        ? {
            label: `${userData.firstname} ${userData.lastname}`,
            value: userData.id,
          }
        : undefined,
    [isSuccess, userData],
  );

  const isAdmin = isSuccess && userData.isAdmin;

  const methods = useForm<ClientFormData>({
    mode: 'onChange',
    resolver: zodResolver(getClientSchema(isTourOperator)),
    defaultValues: {
      agencyName: undefined,
      mainContact: {
        firstName: undefined,
        lastName: undefined,
        email: undefined,
        phoneNumber: undefined,
      },
      secondaryContacts: [],
      address: undefined,
      city: undefined,
      postalCode: undefined,
      country: undefined,
      marginRate: undefined,
      reservationAgent: undefined,
    },
  });

  const { control, setValue, handleSubmit } = methods;

  const mainContact = useWatch({ control, name: 'mainContact' });

  useEffect(() => {
    if (currentAgent) setValue('reservationAgent', currentAgent);
  }, [currentAgent, setValue]);

  const { toastAsync } = useToast();
  const { mutateAsync: createClient } = useCreateClient();

  const submitForm: SubmitHandler<ClientFormData> = async (data) => {
    const { reservationAgent, country, postalCode, marginRate, ...clientData } =
      data;

    const parsedMarginRate = MarginRateSchema.safeParse(marginRate);
    const { status } = await toastAsync(
      createClient({
        ...clientData,
        postalCode: postalCode ? String(postalCode) : undefined,
        reservationAgentId: reservationAgent.value,
        country: country?.value ?? undefined,
        marginRate: parsedMarginRate.success
          ? parsedMarginRate.data
          : undefined,
      }),
      {
        success: tLabel('clientCreatedSuccess'),
      },
    );

    if (status < 400) navigate(PATHS.distributors.clients.list);
  };

  return (
    <FormProvider {...methods}>
      <StyledContainer>
        <StyledFormHeaderWithBreadcrumb
          title={
            mainContact.firstName && mainContact.lastName
              ? `${mainContact.firstName} ${mainContact.lastName}`
              : tLabel('newClient')
          }
          breadcrumb={
            <StyledButton
              variant="link"
              title={tLabel('backToList')}
              className="text-sm text-[#3D3D3D] pl-0"
              onClick={() => navigate(PATHS.distributors.clients.list)}
              icon={<ArrowLeftIcon />}
            />
          }
        >
          <StyledButton
            title={t('common.cancel')}
            onClick={() => navigate(PATHS.distributors.clients.list)}
            variant="outlined"
            className="font-semibold gap-1 px-3 py-2"
          />
          <StyledButtonLoader
            title={tLabel('addClient')}
            successTitle={t('clients.draftSaved')}
            onClick={handleSubmit(submitForm)}
            className="font-semibold gap-1 px-3 py-2"
          />
        </StyledFormHeaderWithBreadcrumb>
        <HorizontalLine />
        {currentAgent !== undefined && (
          <ClientForm
            control={control}
            agents={agents}
            isAdmin={isAdmin}
            isTourOperator={isTourOperator}
          />
        )}
      </StyledContainer>
    </FormProvider>
  );
};

export default CreateClientPage;
